import '../ActivityActions.scss';
import '../../Signing.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IBaseWithSignProps, ICheckTableRow, ICheckTableSchema } from './IBaseWithSignProps';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { AttachFilesBlock, IAttachFilesBlockRefActions } from '@molecules/AttachFilesBlock/AttachFilesBlock';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { ICertificate } from '@models/certificates/cert';
import { IDocAttachesVersions } from '@models/attaches/IDocAttachesVersions';
import { CadespluginService } from '@services/CadespluginService';
import { ActivityExecutionService } from '@services/actions/ActivityExecutionService';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { getActivityIdFromCompositeId } from '@utils/helpers';
import { latin1ToBase64 } from '@srm/ui/lib/base64';
import { IDocumentData } from '@models/document/IDocumentData';
import { IField, IFieldElem } from '@models/IFormData';
import { DocumentService } from '@services/DocumentService';
import { FormulaManager } from '@utils/managers/FormulaManager';
import { AutoTaskService } from '@services/actions/AutoTaskService';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { checkAttachValidation } from '@utils/documentUtils';
import { IPartsSign } from '@models/attaches/IAttachViewSignModel';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import Box from '@atoms/Box';
import { safeFormatDate } from '@utils/date';
import FatalError, { FatalErrorIcon } from '@srm/ui/fatal-error';
import { API_URL } from '@/http';
import Checkbox from '@srm/ui/checkbox';
import { auth } from '@/auth';
import { getUploadingFileLabel } from '@utils/uploadProgress';
import { stringifyError } from '@srm/core/errors';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { hasSelectedFiles, skipUploadedFiles, UPLOADED_FILES_WARNING } from '@organisms/actions/skipUploadedFiles';
import { REMOTE_CONTROL_EXCLUDE_CLASS } from '@srm/screen-sharing/light';

interface IBaseWithSignFormData {
    comment?: string;
}

const signErrorText = (e: unknown): string => {
    if (typeof e === 'string') return e;
    if (Array.isArray(e)) return e.join('\n');
    return (
        'Ошибка при подписании. Убедитесь, что в браузере установлен и настроен плагин КриптоПро ЭЦП. ' +
        'Проверить работу плагина: https://cryptopro.ru/sites/default/files/products/cades/demopage/cades_bes_sample.html'
    );
};

export const BaseWithSignModal: React.FC<IBaseWithSignProps> = (props: IBaseWithSignProps) => {
    const storeDocData = useTypedSelector((state) => state.document.data);
    // В поисковых папках контекста документа нет: данные документа модалка грузит себе сама,
    // не трогая стор карточки, — закрытое окно не оставляет в нём чужой документ.
    const [loadedDocData, setLoadedDocData] = useState<IDocumentData>();
    const currentDocData = loadedDocData ?? (storeDocData?.id === props.docId ? storeDocData : undefined);

    const childFilesBlock = useRef<IAttachFilesBlockRefActions | null>(null);
    const [attachesCreateModel, setAttachesCreateModel] = useState<IAttachesCreateModel>();
    const [filesSizeExceeded, setFilesSizeExceeded] = useState<boolean>(false);

    const [selectedCertThumb, setSelectedCertThumb] = useState<string>();

    const [errorText, setErrorText] = useState<string>();
    const [warningText, setWarningText] = useState<string>();
    // Подготовка (сертификаты, настройки, перечень файлов, данные документа) и выполнение
    // (проверки, загрузка, подпись, отправка) — разные состояния: подготовку можно бросить
    // закрытием окна, выполнение — нет.
    const [certsLoading, setCertsLoading] = useState<boolean>(true);
    const [modelLoading, setModelLoading] = useState<boolean>(true);
    const [attachListLoading, setAttachListLoading] = useState<boolean>(
        Boolean(props.attachFiles && props.previewAttachFiles),
    );
    const [docLoading, setDocLoading] = useState<boolean>(false);
    const [executing, setExecuting] = useState<boolean>(false);
    const [loadingText, setLoadingText] = useState<string>();
    const [uploadProgress, setUploadProgress] = useState<number>();
    const [submitButtonLabel, setSubmitButtonLabel] = useState<string>();
    const uploadedFiles = useRef(new Set<File>());

    const [certList, setCertList] = useState<ICertificate[]>();

    const [attachesRead, setAttachesRead] = useState<boolean>(false);
    const [attaches, setAttaches] = useState<IDocAttachesVersions[]>();

    const commentIsRequired = props.approveModel.commentIsRequired;

    const formMethods = useForm<IBaseWithSignFormData>({ mode: 'onBlur' });
    const hasEditedComment = useUnsavedFormInput(formMethods, { textFields: ['comment'] });

    const loadCertificates = async () => {
        const serv = new CadespluginService();
        //Если имя активити и поток документа совпадают с нужными кейсами из конфига - ставим алгоритм ГОСТ
        let alg: string[] = [props.defaultAlg];
        let gostCN: string[] = [];
        if (
            props.docFlow &&
            props.gostCases.find(
                (x) =>
                    x.flowName == props.docFlow &&
                    x.activityName?.toLocaleLowerCase() == props.actName?.toLocaleLowerCase(),
            )
        ) {
            gostCN = props.gostCN;
            alg = ['гост', 'gost'];
        }

        return serv.getCertListByAlgSubstring(alg, gostCN);
    };

    useEffect(() => {
        // Закрытое окно ответы подготовки не применяет.
        let ignore = false;
        const controller = new AbortController();
        const reportError = (error: unknown) => {
            if (!ignore) setErrorText(stringifyError(error));
        };

        loadCertificates()
            .then((certs) => {
                if (ignore) return;
                //если серификат один - сразу делаем его вбыранным
                if (certs && certs.length == 1) {
                    setSelectedCertThumb(certs[0].thumbprint);
                }
                setCertList(certs);
            })
            .catch((error: unknown) => {
                if (!ignore) setErrorText(signErrorText(error));
            })
            .finally(() => {
                if (!ignore) setCertsLoading(false);
            });

        const activityExecutionService = new ActivityExecutionService();
        activityExecutionService
            .getActivityActionModel(props.actionKey, props.activityId)
            .then((res) => {
                if (!ignore) formMethods.reset({ comment: res.data.prefilledComment ?? '' });
            })
            .catch(reportError)
            .finally(() => {
                if (!ignore) setModelLoading(false);
            });

        if (!storeDocData || props.docId !== storeDocData.id) {
            setDocLoading(true);
            DocumentService.getData(props.docId, undefined, controller.signal)
                .then((res) => {
                    if (!ignore) setLoadedDocData(res.data);
                })
                .catch(reportError)
                .finally(() => {
                    if (!ignore) setDocLoading(false);
                });
        }

        //получение модели для аттачей
        if (props.attachFiles) {
            const attachServ = new AttachActionsService(props.docId, '-1');
            attachServ
                .getAttachInfo(props.actionKey, props.activityId)
                .then((res) => {
                    if (!ignore) setAttachesCreateModel(res.data);
                })
                .catch(reportError);
            if (props.previewAttachFiles) {
                const actId = getActivityIdFromCompositeId(props.objId);
                attachServ
                    .getDocAttachKeysByActKey(actId, props.actionKey)
                    .then((res) => {
                        if (ignore) return;
                        setAttaches(res.data);
                        setAttachesRead(false);
                    })
                    .catch(reportError)
                    .finally(() => {
                        if (!ignore) setAttachListLoading(false);
                    });
            } else {
                setAttachesRead(true);
            }
        }

        return () => {
            ignore = true;
            controller.abort();
        };
    }, []);

    const prepLabel = attachListLoading
        ? 'Получение перечня файлов...'
        : modelLoading
          ? 'Загрузка настроек...'
          : certsLoading
            ? 'Загрузка сертификатов...'
            : docLoading
              ? 'Загрузка данных документа...'
              : undefined;

    const writeCheckResultToTable = async (
        schema: ICheckTableSchema,
        rowsToAdd: ICheckTableRow[],
        clonedDocData: IDocumentData,
    ) => {
        //let clonedDocData: IDocumentData = JSON.parse(JSON.stringify(currentDocData));
        const tbl: IField | undefined = clonedDocData.fields.find((x) => x.name == schema.name);
        if (tbl) {
            const arr: any[] = tbl.value as any[];
            if (arr) {
                let maxNum = Math.max(...arr.map((x) => x['|NUM'] as number));
                if (maxNum == Number.NEGATIVE_INFINITY) maxNum = 0;

                rowsToAdd.forEach((x) => {
                    maxNum = maxNum + 1;
                    const newCheckRow: any = {
                        '|NUM': maxNum,
                    };
                    newCheckRow[schema.systemCol] = x.system;
                    newCheckRow[schema.resultCol] = x.result;
                    newCheckRow[schema.dateCol] = new Date();
                    arr.push(newCheckRow);
                });
            }

            setLoadingText('Запись результатов проверок...');
            clonedDocData.ignoreHooks = true;
            await DocumentService.saveData(clonedDocData, undefined);
        }
    };

    const checkConditionFormula = async (formula: string): Promise<boolean> => {
        const formulaMgr = new FormulaManager(formula);
        const formulaData: Record<string, IFieldElem> = {};
        currentDocData?.fields.forEach(
            (x) =>
                (formulaData[x.name] = {
                    index: 0,
                    name: '',
                    value: x.value,
                }),
        );
        formulaMgr.Init(formulaData);
        return await formulaMgr.EvalFormulaValues(true, false);
    };

    const getFieldByName = (data: IDocumentData | undefined, value: string): IField | undefined => {
        return data?.fields.find((_) => _.name === value);
    };

    const getFieldsByNames = (data: IDocumentData | undefined, values: string | string[]): IField[] => {
        const fields: IField[] = [];

        if (Array.isArray(values)) {
            values.forEach((item) => {
                const field = getFieldByName(data, item);
                if (field) fields.push(field);
            });
        } else {
            const field = getFieldByName(data, values);
            if (field) fields.push(field);
        }

        return fields;
    };

    const resetSubmitState = () => {
        setExecuting(false);
        setUploadProgress(undefined);
        setSubmitButtonLabel(undefined);
    };

    const onSubmit = async (form: IBaseWithSignFormData) => {
        setErrorText(undefined);
        setWarningText(undefined);
        if (!currentDocData) {
            setErrorText('Не удалось загрузить данные документа.');
            return;
        }

        setUploadProgress(undefined);
        setSubmitButtonLabel(undefined);
        setLoadingText(undefined);
        setExecuting(true);
        try {
            await sign(form);
        } catch (e) {
            // Сбой проверок до подписи (формулы, запись результатов) — окно не должно остаться запертым.
            setErrorText(signErrorText(e));
            console.error(e);
        } finally {
            resetSubmitState();
        }
    };

    const sign = async (form: IBaseWithSignFormData) => {
        const attachServ = new AttachActionsService(props.docId, '-1');
        const cadesServ = new CadespluginService();

        const clonedDocData: IDocumentData = JSON.parse(JSON.stringify(currentDocData));

        const error: { flag: boolean; msg?: string } = {
            flag: false,
            msg: '',
        };

        //Проверка СНИЛС сертификата и дока прямо на фронте перед подписанием
        if (props.certDataChecks && selectedCertThumb) {
            const selectedCert = certList?.find((x) => x.thumbprint == selectedCertThumb);
            if (selectedCert) {
                setLoadingText('Проверка СНИЛС сертификата...');
                const resultTableSchema = props.certDataChecks.resultTableSchema;
                for (let i = 0; i < props.certDataChecks.conditionChecks.length; i++) {
                    const check = props.certDataChecks.conditionChecks[i];

                    //определить по условию, нужна ли проверка
                    let checkIsOk: boolean = false;
                    for (const x of check.conditions) {
                        if (
                            x.flowName == props.docFlow &&
                            x.activityName?.toLocaleLowerCase() == props.actName?.toLocaleLowerCase() &&
                            (!x.formula || (await checkConditionFormula(x.formula))) //или формула не задачи или она "выполняется"
                        )
                            checkIsOk = true;
                    }
                    if (!checkIsOk) continue;

                    const partNames = check.frontCheck.certPartNames.split(',').map((x) => x.trim());
                    const docRequisite = currentDocData?.fields.find((x) => x.name == check.frontCheck.docRequisite);

                    if (docRequisite) {
                        const docRequisiteValue = docRequisite.value as string;
                        const valuesFromCert = partNames
                            .map((part) => cadesServ.getCnPart(selectedCert.rawSubject, part))
                            .filter((x) => x);

                        const checkRowsToWrite: ICheckTableRow[] = [];

                        if (valuesFromCert.length > 0 && docRequisiteValue) {
                            //если вообще есть что сравнивать (непустые значения у дока и у сертификата)

                            const isSnilsCheckFailed: boolean =
                                valuesFromCert.filter((x) => x == docRequisiteValue).length == 0;

                            checkRowsToWrite.push({
                                system: check.frontCheck.systemVal,
                                result: isSnilsCheckFailed
                                    ? check.frontCheck.resultFailVal
                                    : check.frontCheck.resultOkVal,
                            });

                            if (isSnilsCheckFailed) {
                                await writeCheckResultToTable(resultTableSchema, checkRowsToWrite, clonedDocData);
                                setErrorText(check.frontCheck.errorText);
                                return;
                            }
                        }

                        if (check.servCheck?.runAutoTaskNameToCheck) {
                            const dataToSign = currentDocData?.id;
                            if (dataToSign) {
                                setLoadingText('Проверка в СС ЭП...');
                                const encodedDataToSign = latin1ToBase64(dataToSign);

                                const signData = await cadesServ.signData(encodedDataToSign, selectedCertThumb);

                                const serv = new AutoTaskService();

                                try {
                                    const res = await serv.runAutoImport({
                                        name: check.servCheck.runAutoTaskNameToCheck,
                                        docKey: currentDocData?.id,
                                        parameters: [encodedDataToSign, signData.sign],
                                    });

                                    checkRowsToWrite.push({
                                        system: check.servCheck.systemVal,
                                        result: res.data.isSucceded
                                            ? check.servCheck.resultOkVal
                                            : `${check.servCheck.resultFailVal}. ${res.data.message}`,
                                    });

                                    if (!res.data.isSucceded) {
                                        error.flag = true;
                                        error.msg = res.data.message;
                                    }
                                } catch (e) {
                                    error.flag = true;
                                    error.msg = e as string;
                                }
                            }
                        }

                        if (checkRowsToWrite.length > 0)
                            await writeCheckResultToTable(resultTableSchema, checkRowsToWrite, clonedDocData);

                        if (error.flag) {
                            setErrorText(error.msg);
                            return;
                        }
                    }
                }
            }
        }

        //Проверка МЧД по аналогии со СНИЛС
        if (props.mchdDataChecks) {
            const mchdDataChecks = props.mchdDataChecks;
            const resultTableSchema = mchdDataChecks.resultTableSchema;
            for (let i = 0; i < mchdDataChecks.conditionChecks.length; i++) {
                const check = mchdDataChecks.conditionChecks[i];

                //определить по условию, нужна ли проверка
                let checkIsOk: boolean = false;
                for (const x of check.conditions) {
                    if (
                        x.flowName == props.docFlow &&
                        x.activityName?.toLocaleLowerCase() == props.actName?.toLocaleLowerCase() &&
                        (!x.formula || (await checkConditionFormula(x.formula))) //или формула не задачи или она "выполняется"
                    ) {
                        checkIsOk = true;
                    }
                }
                if (!checkIsOk) continue;

                if (check.servCheck.runAutoTaskNameToCheck) {
                    setLoadingText('Проверка в МЧД...');
                    const docRequisites = getFieldsByNames(currentDocData, check.servCheck.docRequisite);
                    if (docRequisites.length > 0) {
                        const serv = new AutoTaskService();
                        const parameters = docRequisites.map((item) => {
                            return item.value as string;
                        });

                        const checkRowsToWrite: ICheckTableRow[] = [];

                        try {
                            const res = await serv.runAutoImport({
                                name: check.servCheck.runAutoTaskNameToCheck,
                                docKey: currentDocData?.id,
                                parameters: parameters,
                            });

                            checkRowsToWrite.push({
                                system: check.servCheck.systemVal,
                                result: res.data.isSucceded
                                    ? check.servCheck.resultOkVal
                                    : `${check.servCheck.resultFailVal}. ${res.data.message}`,
                            });

                            if (!res.data.isSucceded) {
                                error.flag = true;
                                error.msg = res.data.message;
                            }
                        } catch (e) {
                            error.flag = true;
                            error.msg = e as string;
                        }

                        if (checkRowsToWrite.length > 0)
                            await writeCheckResultToTable(resultTableSchema, checkRowsToWrite, clonedDocData);

                        if (error.flag) {
                            setErrorText(error.msg);
                            return;
                        }
                    }
                }
            }
        }

        try {
            if (props.objId && selectedCertThumb) {
                let filesToSend: ISelectedFiles = { files: [], attachProperties: [] };

                if (attachesCreateModel && childFilesBlock) {
                    if (!childFilesBlock?.current?.isValid()) {
                        return;
                    }
                    const f = childFilesBlock?.current?.getData();
                    if (f) filesToSend = f;
                }

                // проверка на обязательность наличия аттачей
                if (
                    props.attachFiles &&
                    props.attachRequired &&
                    (filesToSend?.files === undefined || filesToSend?.files.length === 0)
                ) {
                    setWarningText('Необходимо приложить файл');
                    return;
                }

                if (props.attachRequiredValidation && props.attachRequiredValidation.length > 0) {
                    const checkResult = checkAttachValidation(props.attachRequiredValidation, filesToSend);
                    if (checkResult.length > 0) {
                        const text = checkResult.join('\n');
                        setWarningText(text);
                        return;
                    }
                }

                const pendingFiles = skipUploadedFiles(filesToSend, uploadedFiles.current);
                if (pendingFiles.files.length > 0) {
                    setUploadProgress(0);
                    await attachServ.createAttaches(pendingFiles, (state) => {
                        setUploadProgress(state.progress);
                        setSubmitButtonLabel(
                            state.progress >= 100 ? 'Подписываю файлы...' : getUploadingFileLabel(state),
                        );
                    });
                    pendingFiles.files.forEach((file) => uploadedFiles.current.add(file));
                }

                if (filesToSend?.files?.length > 0) {
                    setUploadProgress(100);
                    setSubmitButtonLabel('Подписываю файлы...');
                }

                const actId = getActivityIdFromCompositeId(props.objId);

                setLoadingText('Получение перечня файлов...');
                const attachesData = attaches
                    ? attaches
                    : (await attachServ.getDocAttachKeysByActKey(actId, props.actionKey)).data;

                for (let index = 0; index < attachesData.length; index++) {
                    const attachData = attachesData[index];

                    const docId = attachData.docId;
                    const attachesIds = attachData.attaches;

                    const signsForAttaches: IPartsSign[] = []; //: {[attachId : string] : [sign: string, algorithm: string]}={};
                    //по всем аттачам
                    for (let i = 0; i < attachesIds.length; i++) {
                        const attachId = attachesIds[i];

                        setLoadingText(`Получение файла №${i + 1}...`);
                        const base64content = await attachServ.getAttachContentInBase64(docId, attachId.attachId);

                        setLoadingText(`Подпись файла №${i + 1}...`);
                        const signWithAlg = await cadesServ.signData(base64content, selectedCertThumb);

                        signsForAttaches.push({
                            algorithm: signWithAlg.algorithm,
                            sign: signWithAlg.sign,
                            attachId: attachId.attachId,
                            docId: docId,
                        });
                    }

                    setLoadingText(`Отправка на сервер...`);

                    const activityExecutionService = new ActivityExecutionService();
                    await activityExecutionService.activityActionWithSign(
                        props.actionKey,
                        actId,
                        signsForAttaches,
                        props.approveModel.actType,
                        form.comment,
                    );
                }
                resetSubmitState();
                if (props.completeHandler) props.completeHandler(true);
                return;
            }
        } catch (e: unknown) {
            setErrorText(signErrorText(e));
            if (uploadedFiles.current.size > 0) setWarningText(UPLOADED_FILES_WARNING);
            console.error(e);
        }
    };

    const onCertSelect = (e: any) => {
        if (e.target.value) setSelectedCertThumb(e.target.value);
    };

    const modalContainerProps: IModalContainerProps = {
        header: props.displayName,
        size: props.modalSize,
        // Электронную подпись ставит сам пользователь: удалённое управление поддержки окно не нажимает.
        className: REMOTE_CONTROL_EXCLUDE_CLASS,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: Boolean(prepLabel) || (executing && uploadProgress === undefined),
        loadingLabel: executing ? loadingText : prepLabel,
        progress: uploadProgress,
        progressLabel: submitButtonLabel,
        closeDisabled: executing,
        hasUnsavedInput: () =>
            hasEditedComment() ||
            hasSelectedFiles(childFilesBlock.current) ||
            (props.previewAttachFiles && attachesRead) ||
            selectedCertThumb !== (certList?.length === 1 ? certList[0].thumbprint : undefined),
        okButtonDisabled:
            (props.previewAttachFiles && !attachesRead) || selectedCertThumb === undefined || filesSizeExceeded,
        errorText: errorText,
        warningText: warningText,
        formMethods: formMethods,
        onSubmit: onSubmit,
        onCancel: () => {
            if (props?.completeHandler) props?.completeHandler(false);
        },
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <div className="certList">
                {certList && certList.length > 0 ? (
                    certList.map((cert, i) => (
                        <Box key={i.toString()} className="certificateBox">
                            <input
                                type="radio"
                                name="certSelection"
                                value={cert.thumbprint}
                                onChange={onCertSelect}
                                checked={cert.thumbprint == selectedCertThumb}
                            ></input>
                            <div>
                                <div>
                                    <b>Владелец сертификата </b>
                                    {cert.subjectName}
                                </div>
                                <div>
                                    <b>Центр выдачи сертификата </b>
                                    {cert.issuerName}
                                </div>
                                <div>
                                    <b>Действителен с </b>
                                    {safeFormatDate(cert.validFrom, 'DD.MM.YYYY')}
                                </div>
                                <div>
                                    <b>Действителен по </b>
                                    {safeFormatDate(cert.validTo, 'DD.MM.YYYY')}
                                </div>
                            </div>
                        </Box>
                    ))
                ) : (
                    <div className="certList--noCertsAvailable">
                        <FatalError
                            icon={<FatalErrorIcon variant="default" size={{ width: 100, height: 100 }} />}
                            title="Нет доступных сертификатов"
                            description=""
                        />
                    </div>
                )}

                {attaches && props.previewAttachFiles ? (
                    <div className="attachesList">
                        {attaches.map((docAttaches) => {
                            return docAttaches.attaches?.map((attach, i) => {
                                let link = `${API_URL}/attachment/download/{documentKey}/{key}?access_token=${auth.pageAccessToken() ?? ''}`;

                                link = link.replace('{key}', attach.attachId.split('_')[0]);
                                link = link.replace('{documentKey}', docAttaches.docId.toString());
                                return (
                                    <Box key={i.toString()} className="attachesBox">
                                        <div>
                                            <div>
                                                <b>
                                                    <a target="_blank" href={link} rel="noopener,noreferrer">
                                                        {attach.title}
                                                    </a>
                                                </b>
                                            </div>
                                        </div>
                                    </Box>
                                );
                            });
                        })}
                        <div>
                            <Checkbox
                                defaultChecked={false}
                                onChange={() => {
                                    setAttachesRead(!attachesRead);
                                }}
                                label={'С файлами ознакомлен'}
                            />
                        </div>
                    </div>
                ) : (
                    <></>
                )}
            </div>

            <TextareaControl
                name="comment"
                label="Комментарий"
                required={commentIsRequired}
                initialRowCount={8}
                control={formMethods.control}
                rules={commentIsRequired ? requiredTextRules() : undefined}
            />

            {attachesCreateModel && (
                <AttachFilesBlock
                    ref={childFilesBlock}
                    model={attachesCreateModel}
                    onChangeFilesSizeExceeded={(value) => {
                        setFilesSizeExceeded(value);
                    }}
                />
            )}
        </ModalContainer>
    );
};
